import axios from 'axios';
import { User } from 'common/interfaces/api';

export const FORUM_COMMENTS_PER_PAGE = 20;
export const FORUM_REPLIES_PER_PAGE = 20;

// Forum comment interface
export interface ForumCommentType {
  id: number;
  comment_number_id: number;
  parent_id?: number;
  parent_comment_number_id?: number;
  text: string;
  created_at: string;
  user_info: User;
  profile_user_id: number;
  is_hideable: boolean;
  reply_count: number;
  like_count: number;
  is_liked: boolean;
  status: 'active';
}

// Forum comments list response
export interface ForumCommentsResponseType {
  data: ForumCommentType[];
}

// Forum replies response (also includes parent comment)
export interface ForumRepliesResponseType {
  data: {
    replies: ForumCommentType[];
    comment: ForumCommentType;
  };
  has_next: boolean;
}

// Get forum comments for a user's profile
export const getForumComments = async (
  userId: number,
  lastCommentId?: number,
): Promise<ForumCommentsResponseType> => {
  const limit = FORUM_COMMENTS_PER_PAGE;
  const params = new URLSearchParams({ limit: String(limit) });
  if (lastCommentId) {
    params.append('last_comment_id', String(lastCommentId));
  }
  const response = await axios.get(`/profiles/${userId}/comments?${params}`);
  return response.data;
};

// Get replies for a forum comment (also returns parent comment)
export const getForumReplies = async (
  commentId: number,
  lastCommentId?: number,
): Promise<ForumRepliesResponseType> => {
  const limit = FORUM_REPLIES_PER_PAGE;
  const params = new URLSearchParams({ limit: String(limit) });
  if (lastCommentId) {
    params.append('last_comment_id', String(lastCommentId));
  }
  const response = await axios.get(
    `/profiles/comments/${commentId}/replies?${params}`,
  );
  return response.data;
};

// Create a new forum comment or reply
export const createForumComment = async (
  userId: number,
  text: string,
  parentId?: number,
): Promise<{ data: ForumCommentType }> => {
  const response = await axios.post(`/profiles/${userId}/comments`, {
    text: text.trim(),
    ...(parentId && { parent_id: parentId }),
  });
  return response.data;
};

// Delete a forum comment
export const deleteForumComment = async (commentId: number): Promise<void> => {
  await axios.delete(`/profiles/comments/${commentId}`);
};

// Like or unlike a forum comment
export const likeForumComment = async (
  profileUserId: number,
  commentId: number,
  action: 'like' | 'unlike',
): Promise<void> => {
  await axios.post('/profiles/comments/reaction', {
    profile_user_id: profileUserId,
    comment_id: commentId,
    action,
  });
};
